import { CardInfo } from '@zooplus/checkout-state';
import { ComponentData } from '@zooplus/tracker-listener/dist/es/types/models/component';

import trustlyBank from '../assets/trustly_bank.png';
import trustlyBank1 from '../assets/trustly_bank_1.png';
import trustlyBank2 from '../assets/trustly_bank_2.png';
import trustlyBank3 from '../assets/trustly_bank_3.png';

import { AppConfig, Issuer, CreditCardData, CustomerType } from '@types-internal';
import { CreditCard, PaymentMethodShortName } from '@types-internal/ModernizePaymentTypes';
import { TrackingData, TrackingEvent } from '@types-internal/TrackingTypes';
import { formatPaymentLists } from '@utils/modernizePaymentUtils';
import { flipObject } from '@utils/utils';

export type ISO3166CountryCode = keyof typeof iso3166CountryCodeMap;
export type IVariant = 'default' | 'selected' | 'error' | 'disabled' | 'focus';
export const sepa = {
  holderName: 'Kim Mustermensch',
  lastFourDigits: '1234',
};

export const creditCardNumberMasker = '**** **** **** 1234';

export const bankAccountDetails = {
  owner: 'Kim Mustermensch',
  number: '1234',
};

const defaultAdyenCardData = {
  pspIdentifierAlternative: null,
  createdAt: null,
  bin: null,
  nonce: null,
  favorite: false,
};

export const creditCardsFromCheckout: CreditCard[] = [
  {
    ...defaultAdyenCardData,
    brand: 'VISA',
    pspIdentifier: '1111',
    psp: 'BRAINTREE',
    holderName: 'Eva longname Petlover',
    expirationMonth: '07',
    expirationYear: '2030',
    maskedNumber: '1111',
    expired: true,
  },
  {
    ...defaultAdyenCardData,
    psp: 'ADYEN',
    brand: 'MASTER_CARD',
    pspIdentifier: '1112',
    holderName: 'John Smith',
    expirationMonth: '07',
    expirationYear: '2033',
    maskedNumber: '3456',
    expired: false,
  },
  {
    ...defaultAdyenCardData,
    psp: 'ADYEN',
    brand: 'MASTER_CARD',
    pspIdentifier: '1113',
    holderName: 'Ben John',
    expirationMonth: '07',
    expirationYear: '2036',
    maskedNumber: '3893',
    expired: false,
  },
  {
    ...defaultAdyenCardData,
    psp: 'ADYEN',
    brand: 'OTHER',
    pspIdentifier: '1114',
    holderName: 'Ben John BC',
    expirationMonth: '08',
    expirationYear: '2039',
    maskedNumber: '2784',
    expired: false,
  },
];

export const cardInfoFromCheckout: CardInfo[] = [
  { paymentMethod: 'CREDIT_CARD', cards: creditCardsFromCheckout.slice(0, 3) },
  { paymentMethod: 'BANCONTACT', cards: [creditCardsFromCheckout[3]] },
];

export const creditCards: { [key: string]: CreditCardData } = {
  visa: {
    pspIdentifier: '11111',
    brand: 'visa',
    holderName: 'Eva longname Petlover',
    expirationMonth: '07',
    expirationYear: '2030',
    lastFourDigits: '1111',
    selected: false,
    expired: false,
    nonce: null,
  },
  mastercard: {
    pspIdentifier: '22222',
    brand: 'mastercard',
    holderName: 'John Smith',
    expirationMonth: '07',
    expirationYear: '2033',
    lastFourDigits: '3456',
    selected: false,
    expired: false,
    nonce: null,
  },
  bancontact: {
    pspIdentifier: '44444',
    brand: 'bcmc',
    holderName: 'Peter Johnson',
    expirationMonth: '07',
    expirationYear: '2033',
    lastFourDigits: '3456',
    selected: false,
    expired: false,
    nonce: null,
  },
  mastercard_expired: {
    pspIdentifier: '33333',
    brand: 'mastercard',
    holderName: 'Peter Johnson',
    expirationMonth: '07',
    expirationYear: '2033',
    lastFourDigits: '3456',
    selected: false,
    expired: true,
    nonce: null,
  },
  bancontact_expired: {
    pspIdentifier: '55555',
    brand: 'bcmc',
    holderName: 'Peter Johnson',
    expirationMonth: '07',
    expirationYear: '2033',
    lastFourDigits: '3456',
    selected: false,
    expired: true,
    nonce: null,
  },
};
export const customerSavedPayment: { [key: string]: CreditCardData[] } = {
  saved: Object.values(creditCards),
  none: [],
};

export const creditCardPSP: { [key: string]: string } = {
  Adyen: 'AK',
  Braintree: 'BK',
};

export const issuers: Issuer[] = [
  {
    issuerId: '271',
    issuerName: 'ING Bank Śląski (PIS)',
    issuerOrder: 1,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/271.png',
  },
  {
    issuerId: '15',
    issuerName: 'BLIK',
    issuerOrder: 2,
    logoUrl:
      '	https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/154.png',
  },
  {
    issuerId: '270',
    issuerName: 'mBank (PIS)',
    issuerOrder: 3,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/270.png',
  },
  {
    issuerId: '275',
    issuerName: 'Santander (PIS)',
    issuerOrder: 4,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/275.png',
  },
  {
    issuerId: '279',
    issuerName: 'Konto Inteligo (PIS)',
    issuerOrder: 5,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/279.png',
  },
  {
    issuerId: '85',
    issuerName: 'Bank Millennium',
    issuerOrder: 6,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/85.png',
  },
  {
    issuerId: '222',
    issuerName: 'NestPrzelew',
    issuerOrder: 7,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/222.png',
  },
  {
    issuerId: '99',
    issuerName: 'Pay with BOŚ',
    issuerOrder: 8,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/99.png',
  },
  {
    issuerId: '280',
    issuerName: 'Alior Bank (PIS)',
    issuerOrder: 9,
    logoUrl:
      'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/onlineBanking_PL/280.png',
  },
];

export const errors = {
  payment_failed: 'Your payment has failed',
  card_expired: 'Your credit card has expired',
};

export const iso3166CountryCodeMap = {
  BE: 'Belgium',
  CH: 'Switzerland',
  DE: 'Germany',
  FI: 'Finland',
  NL: 'Netherlands',
  PL: 'Poland',
  PT: 'Portugal',
  SE: 'Sweden',
};

export const CountryIso3166CodeMap = flipObject(iso3166CountryCodeMap);

export const customerTypes = {
  'Existing customer': 'EC',
  'Guest customer': 'GC',
  'New customer': 'NC',
};

export const countryToPaymentMethodMap: {
  [Property in ISO3166CountryCode]: PaymentMethodShortName[];
} = {
  BE: ['BM', 'BC', 'AP', 'GP', 'K', 'VK', 'PP'],
  CH: ['TW', 'AP', 'K', 'PP', 'R', 'GP'],
  DE: ['RI', 'BS', 'K', 'AP', 'GP', 'PP', 'R', 'VK'],
  FI: ['PT', 'KL', 'KO', 'AP', 'GP', 'K', 'PP', 'VK'],
  NL: ['ID', 'AP', 'GP', 'K', 'VK', 'PP', 'R', 'BM', 'BC'],
  PL: ['OP', 'VK', 'CD', 'AP', 'GP', 'K', 'PP'],
  PT: ['AP', 'GP', 'K', 'CD', 'VK', 'PP', 'MB'],
  SE: ['TY', 'VK', 'KL', 'KO', 'AP', 'GP', 'K', 'PP'],
};

export const PAYMENT_METHOD_NAMES: { [key: string]: string } = {
  K: 'general.payment.credit_card',
  AP: 'general.payment.applepay',
  BC: 'general.payment.bancontact_card',
  BM: 'Appli mobile \n Payconiq by Bancontact',
  BS: 'general.payment.direct_debit_sepa',
  CD: 'general.payment.cashondelivery',
  DP: 'general.payment.dotpay',
  ID: 'general.payment.ideal',
  KL: 'general.payment.klarna_pay_later',
  KO: 'general.payment.klarna_pay_over_time',
  MB: 'general.payment.multibanco',
  OP: 'general.payment.online_banking_pl',
  PP: 'general.payment.paypal',
  PT: 'general.payment.paytrail',
  R: 'general.payment.invoice',
  RI: 'general.payment.invoice',
  TW: 'general.payment.twint',
  TY: 'general.payment.trustly',
  VK: 'general.payment.prepayment',
};

export const countryCodes = Object.keys(countryToPaymentMethodMap);
export const PMConfig: any = {
  Adyen: {
    clientInfo: {
      pspName: 'ADYEN',
      pspToken: 'test_ORNCOIYKLJGFROSKKDM267OYGMGFV4XV',
    },
  },
  Bancontact: {
    clientInfo: {
      pspName: 'ADYEN',
      pspToken: 'test_ORNCOIYKLJGFROSKKDM267OYGMGFV4XV',
      // pspToken:
      //   '10001|CEA4D269C3ACAC4D6242A27D64B3A0B96B555BE9243B8EA7A11929A9BAF8078442DE085F7412A8F3929F73D2BEB3B9FBCD3C21889537D3FF1799EB18AE780EB45A4CA98C826103BE06B9EC335536E09DC8D5DDDCCDFAD7E88988DF679D472A49D0E76FDE95D49C12A0C8F09F6DC4BA34FB04F184A4A22EAB0D4B83F6B4A23ACC39C609BB381CD72A539B10BCE50482D8B4710404FF33766EB102F7C1BB0CEB3DA9ECAEFE7EE2D18135D4BAEEA73DBE6FE1B88C1FAE4BD90464081F452226433E6C9A8CEDF93B0000E1735BED8BB8C33C6C56E926E8D43607D9204851F184472ACF642D1B028DE4CC85A80A25A2EED759D71E1713C1BACD53F629AFA2D130C3BF',
    },
  },
  Braintree: {
    clientInfo: {
      pspName: 'BRAINTREE',
      pspToken:
        'eyJ2ZXJzaW9uIjoyLCJhdXRob3JpemF0aW9uRmluZ2VycHJpbnQiOiJleUpyYVdRaU9pSXlNREU0TURReU5qRTJMWE5oYm1SaWIzZ2lMQ0pwYzNNaU9pSm9kSFJ3Y3pvdkwyRndhUzV6WVc1a1ltOTRMbUp5WVdsdWRISmxaV2RoZEdWM1lYa3VZMjl0SWl3aVlXeG5Jam9pUlZNeU5UWWlmUS5leUpsZUhBaU9qRTNOalF4TlRBeU9UY3NJbXAwYVNJNkltRmtaRGd6TkRGbUxURmxOakl0TkRjd1pDMWlZVFpqTFdNeVlUVTBPREprTURNd1lTSXNJbk4xWWlJNklqbG9OSEY1Y0hKNmMyTnROWEF5TXpJaUxDSnBjM01pT2lKb2RIUndjem92TDJGd2FTNXpZVzVrWW05NExtSnlZV2x1ZEhKbFpXZGhkR1YzWVhrdVkyOXRJaXdpYldWeVkyaGhiblFpT25zaWNIVmliR2xqWDJsa0lqb2lPV2cwY1hsd2NucHpZMjAxY0RJek1pSXNJblpsY21sbWVWOWpZWEprWDJKNVgyUmxabUYxYkhRaU9tWmhiSE5sTENKMlpYSnBabmxmZDJGc2JHVjBYMko1WDJSbFptRjFiSFFpT21aaGJITmxmU3dpY21sbmFIUnpJanBiSW0xaGJtRm5aVjkyWVhWc2RDSmRMQ0p6WTI5d1pTSTZXeUpDY21GcGJuUnlaV1U2Vm1GMWJIUWlMQ0pDY21GcGJuUnlaV1U2UTJ4cFpXNTBVMFJMSWwwc0ltOXdkR2x2Ym5NaU9uc2liV1Z5WTJoaGJuUmZZV05qYjNWdWRGOXBaQ0k2SWxwdmIzQnNkWE5EU0NKOWZRLnpOS2VYZGY1a01XYURLSFBQb2tRMDEySU9VZ1lrSjFHYTFUOG10SGtOa2lVb1lLRzR4ZGpWT1N2ekI5bHp6UVlWdkMtRXRYZ3p2Tmg0WnlVNHFrNVlRIiwiY29uZmlnVXJsIjoiaHR0cHM6Ly9hcGkuc2FuZGJveC5icmFpbnRyZWVnYXRld2F5LmNvbTo0NDMvbWVyY2hhbnRzLzloNHF5cHJ6c2NtNXAyMzIvY2xpZW50X2FwaS92MS9jb25maWd1cmF0aW9uIiwibWVyY2hhbnRBY2NvdW50SWQiOiJab29wbHVzQ0giLCJncmFwaFFMIjp7InVybCI6Imh0dHBzOi8vcGF5bWVudHMuc2FuZGJveC5icmFpbnRyZWUtYXBpLmNvbS9ncmFwaHFsIiwiZGF0ZSI6IjIwMTgtMDUtMDgiLCJmZWF0dXJlcyI6WyJ0b2tlbml6ZV9jcmVkaXRfY2FyZHMiXX0sImNsaWVudEFwaVVybCI6Imh0dHBzOi8vYXBpLnNhbmRib3guYnJhaW50cmVlZ2F0ZXdheS5jb206NDQzL21lcmNoYW50cy85aDRxeXByenNjbTVwMjMyL2NsaWVudF9hcGkiLCJlbnZpcm9ubWVudCI6InNhbmRib3giLCJtZXJjaGFudElkIjoiOWg0cXlwcnpzY201cDIzMiIsImFzc2V0c1VybCI6Imh0dHBzOi8vYXNzZXRzLmJyYWludHJlZWdhdGV3YXkuY29tIiwiYXV0aFVybCI6Imh0dHBzOi8vYXV0aC52ZW5tby5zYW5kYm94LmJyYWludHJlZWdhdGV3YXkuY29tIiwidmVubW8iOiJvZmYiLCJjaGFsbGVuZ2VzIjpbXSwidGhyZWVEU2VjdXJlRW5hYmxlZCI6dHJ1ZSwiYW5hbHl0aWNzIjp7InVybCI6Imh0dHBzOi8vb3JpZ2luLWFuYWx5dGljcy1zYW5kLnNhbmRib3guYnJhaW50cmVlLWFwaS5jb20vOWg0cXlwcnpzY201cDIzMiJ9LCJwYXlwYWxFbmFibGVkIjpmYWxzZX0=',
    },
  },
};
export const paymentMethodList = formatPaymentLists(
  countryToPaymentMethodMap,
  issuers,
  undefined,
  ['K', 'PP', 'AP', 'GP'],
  ['VK', 'TW', 'R'],
  'ADYEN',
  PMConfig,
);

export const preferredPaymentMethods = {
  logo: ['PP', 'AP', 'GP', 'TY', 'TW', 'MB', 'PT', 'DP', 'ID'],
  logoText: ['CD', 'VK', 'R'],
  creditCard: ['RI', 'K', 'BC', 'BS'],
  logoPmOption: ['KL', 'KO'],
  cobrandLogoPmOption: ['OP'],
};

export const trustly_info = [
  'checkout.payment.trustly.info',
  'checkout.payment.trustly.info1',
  'checkout.payment.trustly.info2',
];

/*
 * Simple Legal text for payment methods
 * The keys are the short names of the payment methods
 * The values are the translation keys for the legal text
 * Payment methods with no text either don't have legal text or have a more complex one on the PaymentInfoSwitch component
 */
export const legal_text: { [key in Partial<PaymentMethodShortName>]: string } = {
  AP: '',
  GP: '',
  CD: 'payment.cashondelivery.full.description',
  OP: 'payment.online_banking_pl.full.description',
  KL: 'payment.KL.full.description',
  KO: 'payment.KO.full.description',
  PP: 'payment.paypal.full.description',
  VK: 'payment.prepayment.full.description',
  TW: 'payment.twint.full.description',
  PT: 'payment.paytrail.full.description',
  BM: 'payment.bancontact_mobile.full.description',
  BC: 'payment.bancontact_card.full.description',
  BS: 'payment.sepa.full.description',
  DP: '',
  MB: '',
  R: 'payment.invoice.full.description',
  RI: 'payment.riverty_invoice.full.description',
  ID: 'payment.ideal.full.description',
  TY: '',
  K: 'checkout.payment.creditcard.3d.secure.message',
};
export const trustly_supported_banks = [trustlyBank, trustlyBank1, trustlyBank2, trustlyBank3];

export const appConfig: AppConfig = {
  shop: {
    domain: 'zooplus.de',
    locales: ['zu'],
    siteId: 15,
  },
  locale: 'zu',
  realm: 'zooplus',
  environment: 'dev',
};

export const payment_error_translations_map = {
  APPLEPAY_NOT_SUPPORTED: 'payment.applepay.not.supported',
  GOOGLEPAY_NOT_SUPPORTED: 'payment.googlepay.not.supported',
  APPLE_PAY_PAYMENT_UNSUCCESSFUL_ERROR: 'payment.method.submit.failed',
  GOOGLE_PAY_PAYMENT_UNSUCCESSFUL_ERROR: 'payment.method.submit.failed',
  BRAINTREE_PAYMENT_UNSUCCESSFUL_ERROR: 'payment.method.submit.failed',
  UNKNOWN_ERROR: 'payment.general.error.processing',
  'ZPPAPI-T-01': 'payment.technical.issues',
  'ZPPAPI-T-02': 'payment.technical.issues',
  'ZPPAPI-T-03': 'payment.technical.issues',
  'ZPPAPI-T-04': 'payment.technical.issues',
  'ZPPAPI-T-05': 'payment.technical.issues',
  'ZPPAPI-T-06': 'payment.technical.issues',
  CHECKOUT_PAYMENT_ERROR_NEWBANCONTACTCARDNEEDED: 'checkout.payment.bancontact.error.newcardneeded',
  CHECKOUT_PAYMENT_ERROR_NEWCREDITCARDNEEDED: 'checkout.payment.creditcard.error.newcardneeded',
  CHECKOUT_PAYMENT_ERROR_BANCONTACTCARDEXPIRED: 'checkout.payment.bancontact.error.expired',
  CHECKOUT_PAYMENT_ERROR_CREDITCARDEXPIRED: 'checkout.payment.creditcard.error.expired',
  CHECKOUT_PAYMENT_ERROR_ISSUERINVALID: 'checkout.payment.error.issuerinvalid',
  CHECKOUT_PAYMENT_ERROR_PREFERREDPAYMENTDISABLED:
    'checkout.payment.error.preferred.payment.disabled',
  PREFERRED_PAYMENT_METHOD_TEMPORARILY_UNAVAILABLE:
    'checkout.preferred.payment.method.temporarily.unavailable',
};

export const payment_error_codes = Object.keys(payment_error_translations_map);

export const MOBILE_BREAKPOINT_UPTO_3_PM = 'mobile-breakpoint-upto-three-pm';
export const MOBILE_BREAKPOINT_4_PM = 'mobile-breakpoint-four-pm';
export const MOBILE_BREAKPOINT_MORETHAN_4_PM = 'mobile-breakpoint-morethan-four-pm';
export const DESKTOP_BREAKPOINT_UPTO_5_PM = 'desktop-breakpoint-upto-five-pm';
export const DESKTOP_BREAKPOINT_6_PM = 'desktop-breakpoint-six-pm';
export const DESKTOP_BREAKPOINT_MORETHAN_6_PM = 'desktop-breakpoint-morethan-six-pm';
export const DESKTOP_BREAKPOINT_MORETHAN_8_PM = 'desktop-breakpoint-morethan-eight-pm';

export const TRACKING_TEST_DATA: {
  sid: string;
  currency: string;
  siteId: number;
  customerId: string;
  customerType: CustomerType;
} = {
  sid: 'AB58464A5DEB1E656CB393085D624A2F-1734649053140',
  currency: 'GBP',
  siteId: 8,
  customerId: 'AB58464A5DEB1E656CB393085D624A2F-1734649053140',
  customerType: 'EXISTING_CUSTOMER',
};

export const APPLE_PAY_CONTAINER_ID = 'applepay-container';
export const GOOGLE_PAY_CONTAINER_ID = 'googlepay-container';
export const PAYMENT_SAVE_BUTTON_ID = 'payment-save-button';
export const PAYMENT_DISCARD_BUTTON_ID = 'payment-discard-button';
export const CLOSE_MODAL_BUTTON = 'click_close_modal';

export const EVENTS_MAP: {
  [key in TrackingEvent]: {
    event: TrackingData['event'];
    page: TrackingData['page'];
    target?: ComponentData;
    component?: ComponentData;
  };
} = {
  Braintree_3DSWindow_Displayed: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'Braintree_3DSWindow',
      id: 'braintree_3dswindow_displayed',
    },
  },
  Click_3DSCancel: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'Braintree_3DS_Modal',
      id: 'click_3dscancel',
    },
  },
  '3DSsubmitOK': {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_3DS_display',
      id: '3ds_submit_ok',
    },
  },
  '3DSsubmitKO': {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_3DS_display',
      id: '3ds_submit_ko',
    },
  },
  '3DSsubmitKObackend': {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_3DS_display',
      id: '3ds_submit_kobackend',
    },
  },
  force3DSchallenge: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_3DS_display',
      id: 'force_3ds_challenge',
    },
  },
  AdyenFormOK: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'modernize_payment_component_Save_button',
      id: 'adyen_form_ok',
    },
  },
  AdyenFormKO: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'modernize_payment_component_Save_button',
      id: 'adyen_form_ko',
    },
  },
  BCCardFormOK: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'modernize_payment_component_Save_button',
      id: 'bc_card_form_ok',
    },
  },
  BCCardFormKO: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'modernize_payment_component_Save_button',
      id: 'bc_card_form_ko',
    },
  },
  PaymentSheetOpen: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'ApplePay_BuyButton',
      id: 'payment_sheet_open',
    },
  },
  PaymentSheetCloseOK: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'ApplePay_PaymentSheet',
      id: 'payment_sheet_close_ok',
    },
  },
  PaymentSheetCloseKO: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'ApplePay_PaymentSheet',
      id: 'payment_sheet_close_ko',
    },
  },
  Click_CloseModal: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'Braintree_3DS_error_Modal',
      id: CLOSE_MODAL_BUTTON,
    },
  },
  Click_PayPal_authoshipment_agreement: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'Click_PayPal_authoshipment_agreement',
      id: 'click_paypal_authoshipment_agreement',
    },
  },
  PaypalModalDisplayed: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'PayPal_Modal',
      id: 'paypal_modal_displayed',
    },
  },
  PaypalModalNotDisplayed: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'PayPal_Modal',
      id: 'paypal_modal_not_displayed',
    },
  },
  ['paypal agr KO']: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'PayPal_agreement_Err',
      id: 'paypal_agr_ko',
    },
  },
  ['paypal agr OK']: {
    event: 'IMPRESSION',
    page: 'CHECKOUT_PREVIEW',
    target: {
      name: 'PayPal_agreement_OK',
      id: 'paypal_agr_ok',
    },
  },

  '3DSskipOK': {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_3DS_display',
      id: '3ds_skip_ok',
    },
  },
  subscriptionFirst: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_BuyButton',
      id: 'subscription_first',
    },
  },
  cardType: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'Braintree_BuyButton',
      id: 'card_type',
    },
  },
  BTformKO: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'SaveUpdate_Payment_Button',
      id: 'bt_form_ko',
    },
  },
  BTformOK: {
    event: 'INTERACTION',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'SaveUpdate_Payment_Button',
      id: 'bt_form_ok',
    },
  },
  ChangePaymentMethod: {
    event: 'CLICK',
    page: 'CHECKOUT_PREVIEW',
    component: {
      name: 'modernize_payment_component_Save_button',
      id: 'change_payment_method',
    },
  },
};

export const testPaymentMethod = {
  paymentMethod: 'CREDIT_CARD',
  paymentMethodShortName: 'K',
  availableForGuest: true,
  order: 1,
  preferredPayment: false,
  autoshipmentEligible: false,
  disabled: false,
  default: true,
};

export const ERROR_CONTAINER_ID = 'error-container';

export const googlePaylocalPaymentMethods = {
  paymentMethods: [
    {
      brands: ['visa', 'mc'],
      name: 'Credit Card',
      type: 'scheme',
    },
    {
      brands: ['visa', 'mc', 'girocard'],
      configuration: {
        merchantId: '50',
        gatewayMerchantId: 'ZooplusGooglePayEUR',
      },
      name: 'Google Pay',
      type: 'googlepay',
    },
  ],
  countryCode: 'GB',
};

export const GOOGLE_PAY_BUTTON_ID = 'adyen-google-pay-button';
